import {Injectable} from '@angular/core';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {Agenda} from '../models/agenda';
import {Department} from '../models/department';
import {Activity, Topic} from '../models/event';
import {Member} from '../models/member';
import {SpinnerService} from '../shared/waiting/spinner.service';
import {catchError, from, lastValueFrom, map, Observable, of} from 'rxjs';
import {PublishService} from './publish.service';

interface Speaker {
  id: string;
  name: string;
  [ key: string ]: any;
}

@Injectable({
  providedIn: 'root'
})
export class EventPublishService {

  public currentPublishCollection = 'sbccv_congress_published';
  // public currentPublishCollection = 'sbccv_congress_published_test';

  constructor (
    private db: AngularFirestore,
    private _spinnerService: SpinnerService,
    private _publishService: PublishService
  ) { }

  async geteAllData (): Promise<any[]> {
    try {
      // 1. Substituição do .toPromise() pelo lastValueFrom
      // Mantemos a ordenação tripla solicitada
      const querySnapshot = await lastValueFrom(
        this.db.collection<any>(this.currentPublishCollection, ref => ref
          .orderBy("context_id", "asc")
          .orderBy("date", "asc")
          .orderBy("startTime", "asc")
        ).get()
      );

      // 2. Otimização com .map()
      // O .map() é mais eficiente que criar um array vazio e dar push dentro de um loop
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

    } catch (error) {
      // 3. Melhoria no log para facilitar o debug
      console.error(`Erro em geteAllData na coleção ${ this.currentPublishCollection }:`, error);
      throw error;
    }
  }

  async getAllSpeakers (): Promise<any[]> {
    try {
      // 1. Substituição do .toPromise() pelo lastValueFrom
      // Mantemos a ordenação tripla solicitada
      const querySnapshot = await lastValueFrom(
        this.db.collection<any>('sbccv_speakers_published', ref => ref
          .orderBy("name", "asc")
        ).get()
      );

      // 2. Otimização com .map()
      // O .map() é mais eficiente que criar um array vazio e dar push dentro de um loop
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

    } catch (error) {
      // 3. Melhoria no log para facilitar o debug
      console.error(`Erro em geteAllData na coleção ${ this.currentPublishCollection }:`, error);
      throw error;
    }
  }

  async getMasterEventByKey (key: string): Promise<any[]> {
    const array: any[] = [];
    try {
      const querySnapshot = await this.db.collection<any>('sbccv_events', ref => ref
        .where("key", "==", key)
        .where("master", "==", true))
        .get()
        .toPromise();

      querySnapshot.forEach((doc) => {
        // Adiciona cada documento ao array
        array.push({
          "id": doc.id,
          ...doc.data()
        });
      });
      // Retorna o array após carregado
      return array[ 0 ];
    } catch (error) {
      console.error("Erro getMasterEvent: ", error);
      throw error;
    }
  }

  selectByKey (key: any) {
    return this.db.collection<Event>('sbccv_events', ref => ref
      .where('deleted', '==', false)
      .where('key', '==', key)
    ).get()
  }

  getDays (event_id: any) {
    return this.db.collection<Event>('sbccv_event_days', ref => ref
      .where('event_id', '==', event_id)
      .where('deleted', '==', false)
    ).get()
  }

  async getAgenda (event_id) {
    return this.db.collection<Agenda>('sbccv_agenda', ref => ref
      .where('event_id', '==', event_id)
      .orderBy('day_date')
    ).get();
  }

  async getLocation (location_id) {
    return this.db.doc<Location>('sbccv_convention_centers/' + location_id)
      .get();
  }

  async getDeptos () {
    return this.db.collection<Department>('sbccv_departments', ref => ref.orderBy('short', 'asc')).get();
  }

  async getActivities (event_id) {
    return this.db.collection<Activity>('sbccv_event_activities', ref => ref
      .where('deleted', '==', false)
      .where('event_id', '==', event_id)
      .orderBy('date')
    ).get();
  }

  async getTopics (event_id) {
    return this.db.collection<Topic>('sbccv_event_topics', ref => ref
      .where('deleted', '==', false)
      .where('event_id', '==', event_id)
    ).get();
  }

  compareNames (a, b) {
    if (a.name > b.name) return 1;
    if (b.name > a.name) return -1;
    return 0;
  }


  compareLocations (a: any, b: any) {
    if (a.location > b.location) return 1;
    if (b.location > a.location) return -1;
    return 0;
  }

  compareDayAndTime (a: any, b: any) {

    const da = new Date(a.date)
    const db = new Date(b.date)

    const _a = da.getTime() + a.startTime.replace(':', '')
    const _b = db.getTime() + b.startTime.replace(':', '')

    if (_a > _b) return 1;
    if (_b > _a) return -1;

    return 0;

  }

  compareTimes (a, b) {
    let _a = a.startTime
    let _b = b.startTime
    if (_a > _b) return 1;
    if (_b > _a) return -1;
    return 0;
  }

  formatDate (date: any) {
    if (date) {
      let dt: Date = date.toDate()
      let dd: any = dt.getDate()
      let mm: any = dt.getMonth() + 1
      let yy: any = dt.getFullYear()

      if (dd.toString().length < 2) dd = "0" + dd
      if (mm.toString().length < 2) mm = "0" + mm

      return dd + "/" + mm + "/" + yy
    } else {
      return ""
    }
  }

  getSpeakerData (member_id) {
    return this.db.doc<Member>('sbccv_speakers_published/' + member_id)
      .get();
  }
  //


  getSpeaker (speaker_id) {
    return this.db.doc<Member>('sbccv_speakers_published/' + speaker_id)
      .get();
  }

  async getSpeakerStaff (id: any): Promise<any> {
    try {
      const querySnapshot: any = await this.db.collection<any>('sbccv_members')
        .doc(id)
        .get()
        .toPromise();

      if (!querySnapshot.exists) {
        return null;
      }

      return {
        "id": querySnapshot.id,
        'name': querySnapshot.data().name,
        'state': querySnapshot.data().state,
        'country': querySnapshot.data().country,
        'image': querySnapshot.data().image,
        'text': querySnapshot.data().text
        // ...querySnapshot.data()
      };

    } catch (error) {
      console.error('Error getSpeakerStaff: ', error);
      throw error;
    }
  }

  
  async getSpeakers (): Promise<any[]> {
    try {
      const querySnapshot = await this.db.collection('sbccv_speakers_published').get().toPromise();

      if (!querySnapshot) {
        return [];
      }

      return querySnapshot.docs.map((doc: any) => ({
        ...doc.data(),
        id: doc.id,
      }));

    } catch (error) {
      console.error('Error getting speakers:', error);
      throw error; // Ou retorne um array vazio se preferir tratamento silencioso
    }
  }


  async clearActivities (context_id: string): Promise<boolean> {
    const waiting = this._spinnerService.show();

    try {
      // 1. Busca os documentos (usando lastValueFrom para evitar o deprecated)
      const querySnapshot = await lastValueFrom(
        this.db.collection(this.currentPublishCollection, ref =>
          ref.where('context_id', '==', context_id)
        ).get()
      );

      if (querySnapshot.empty) return true;

      const docs = querySnapshot.docs;
      const totalDocs = docs.length;
      const batchLimit = 500; // Limite oficial do Firebase

      // 2. Loop para processar os documentos em grupos de 500
      for (let i = 0; i < totalDocs; i += batchLimit) {
        const batch = this.db.firestore.batch();

        // Obtém o próximo pedaço (chunk) de 500 documentos
        const chunk = docs.slice(i, i + batchLimit);

        chunk.forEach(doc => {
          batch.delete(doc.ref);
        });

        // Comita o lote atual antes de ir para o próximo
        await batch.commit();
        console.log(`Lote processado: documentos ${ i + 1 } a ${ Math.min(i + batchLimit, totalDocs) }`);
      }

      return true;
    } catch (error) {
      console.error('Error in clearActivities:', error);
      return false;
    } finally {
      waiting.close();
    }
  }

  async publishActivities (activities: any[]): Promise<boolean> {
    const waiting = this._spinnerService.show();
    // console.log(JSON.stringify(activities))

    try {
      // 1. Cria um array de promises para todas as operações de inserção
      const publishPromises = activities.map(activity =>
        this.db.collection(this.currentPublishCollection)
          .add({...activity})
      );

      // 2. Aguarda todas as inserções serem concluídas
      await Promise.all(publishPromises);
      return true;
    } catch (error) {
      console.error('Error publishing activities:', error);
      return false;
    } finally {
      waiting.close()
    }
  }

  async getSchedule (context_id: any): Promise<any[]> {
    try {
      const querySnapshot = await this.db.collection(this.currentPublishCollection, ref => ref
        .where('context_id', '==', context_id)
        .orderBy('date', 'asc')
        .orderBy('location', 'asc')
        .orderBy('startTime', 'asc'))
        .get()
        .toPromise();

      if (!querySnapshot) {
        return [];
      }

      return querySnapshot.docs.map((doc: any) => ({
        id: doc.id,
        ...doc.data()
      }));

    } catch (error) {
      console.error('Error getSchedule:', error);
      throw error; // Ou retorne um array vazio se preferir tratamento silencioso
    }
  }


  makeSchedule (data: any) {
    return data;
  }

  formatDate_DD_MM (dt: any) {

    if (!dt) {
      return ""
    }

    const date: Date = new Date(dt)

    const dd = date.getDate()
    const MM = date.getMonth() + 1

    let mm: string = MM.toString()

    if (mm.length < 2)
      mm = "0" + MM

    return dd + "/" + mm

  }

  async publishSpeakers (speakersInAgenda: any[]) {
    const waiting = this._spinnerService.show();

    try {
      // carregas os publishes de sbccv_publish_speakers
      const speakersPublished = await this.getSpeakers()

      // currentSpeakers = publishers in agenda
      for (let idx = 0; idx < speakersInAgenda.length; idx++) {
        const currentSpeaker = speakersInAgenda[ idx ];
        if (currentSpeaker.name !== "A DEFINIR") {
          //
          const alreadyPublished = speakersPublished.find((e) => {
            return e.id == currentSpeaker.id;
          })
          //
          if (!alreadyPublished) {
            const member = await this.getSpeakerStaff(currentSpeaker.id)
            if (member) {
              this.db.collection('sbccv_speakers_published').doc(member.id)
                .set(Object.assign({}, member), {'merge': true})
                .then(() => { })
                .catch(() => {
                  throw new Error("update speaker error");
                })
            }

          }
        }

      }
      return true;
      //
    } catch (error) {
      //
      console.error('Error publishing speakers:', error);
      return false;
      //
    } finally {
      waiting.close();
    }

  }

  async clearSpeakersAgenda (context_id: string): Promise<boolean> {
    const waiting = this._spinnerService.show();

    try {
      // 1. Busca os documentos (usando lastValueFrom para evitar o deprecated)
      const querySnapshot = await lastValueFrom(
        this.db.collection("pub_speakers_agenda", ref =>
          ref.where('context_id', '==', context_id)
        ).get()
      );

      if (querySnapshot.empty) return true;

      const docs = querySnapshot.docs;
      const totalDocs = docs.length;
      const batchLimit = 500; // Limite oficial do Firebase

      // 2. Loop para processar os documentos em grupos de 500
      for (let i = 0; i < totalDocs; i += batchLimit) {
        const batch = this.db.firestore.batch();

        // Obtém o próximo pedaço (chunk) de 500 documentos
        const chunk = docs.slice(i, i + batchLimit);

        chunk.forEach(doc => {
          batch.delete(doc.ref);
        });

        // Comita o lote atual antes de ir para o próximo
        await batch.commit();
        console.log(`Lote processado clearSpeakersAgenda: documentos ${ i + 1 } a ${ Math.min(i + batchLimit, totalDocs) }`);
      }

      return true;
    } catch (error) {
      console.error('Error in clearSpeakersAgenda:', error);
      return false;
    } finally {
      waiting.close();
    }
  }

  async publishSpeakersAgenda (master: any): Promise<boolean> {
    //
    const waiting = this._spinnerService.show();
    //
    try {
      //
      const agenda = await this._publishService.getSpeakersAgenda(master.key);
      console.log("Agenda: " + JSON.stringify(agenda))
      // 1. Cria um array de promises para todas as operações de inserção
      /*
      const publishPromises = agenda.map(entry =>
        this.db.collection("pub_speakers_agenda")
          .add({...entry})
      );
      */

      // 2. Aguarda todas as inserções serem concluídas
      // await Promise.all(publishPromises);

      return true;
    } catch (error) {
      console.error('Error publishing speakersAgenda:', error);
      return false;
    } finally {
      waiting.close()
    }

  }

}
